import { FC, useCallback, useRef } from 'react';
import { Link } from '@remix-run/react';
import { ArticleDetailUtil, EArticleClass, IUserDetail, TArticleDetail, TComment } from 'shared/models';
import { ScopedProvider, useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { ImmersivePostItemAtoms } from '@discover/body/immersive-post/immersive-post-item/atoms';
import {
  ArticleBody,
  ArticleComments,
  ArticleInfoBar,
  ArticleLargeImage,
  ArticleVideo,
  AuthorBar,
  SAR,
} from './components';
import { ArticleGallery } from '~/components/article-gallery';
import { EArticleGalleryLayoutStrategy } from '~/components/article-gallery/atoms';
import { useAtomValue, useSetAtom } from 'jotai';
import { $DiscoverArticleRelatedPost } from 'shared/atoms';
import { useRouteLoader } from '~/utils/use-route-loader';
import { loadable } from 'jotai/utils';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition } from '~/utils/click-position';
import cls from 'classnames';

import './style.scss';
import { ViewObserverPreset, useViewObserver } from '@lemon8/web-app-shared/util';
import { $DiscoverShownPostCursor } from '@discover/shared/atom';
import { useSEOHref } from '~/utils/build-seo-url';

interface IImmersivePostItemProps {
  rank: number;
  article: TArticleDetail;
  author: IUserDetail;
  comments: TComment[];
  beforeHotKeywordsModule?: boolean;
  beforeGuessWordsCards?: boolean;
  beforeHashtagArticleCards?: boolean;
}

const ImmersivePostItemImpl: FC = () => {
  const article = useScopedAtomValue(ImmersivePostItemAtoms.$CurrentArticle);
  const additionalParams = useScopedAtomValue(ImmersivePostItemAtoms.$RelatedShowAdditionalParams);
  const beforeGuessWordsCards = useScopedAtomValue(ImmersivePostItemAtoms.$BeforeGuessWordsCards);
  const beforeHotKeywordsModule = useScopedAtomValue(ImmersivePostItemAtoms.$BeforeHotKeywordsModule);
  const beforeHashtagArticleCards = useScopedAtomValue(ImmersivePostItemAtoms.$BeforeHashtagArticleCards);
  const setDiscoverShownPostCursor = useSetAtom($DiscoverShownPostCursor);
  const postHref = useSEOHref(ArticleDetailUtil.getArticleHref(article));

  const { articleClass, groupId } = article;
  const discoverArticleRelatedPost = useAtomValue(loadable($DiscoverArticleRelatedPost(groupId)));

  const articleRelatedPost =
    discoverArticleRelatedPost.state === 'hasData'
      ? discoverArticleRelatedPost.data
      : {
          items: [],
          searchId: '',
          cursor: 0,
          hasMore: true,
        };

  const loader = useRouteLoader(
    'get-article-related-post',
    {
      group_id: groupId,
    },
    {
      atomFamilies: {
        $DiscoverArticleRelatedPost,
      },
    },
  );

  const loaderCalled = useRef(false);
  const loadArticleRelatedPost = useCallback(() => {
    if (!loaderCalled.current) {
      loader.load();
      loaderCalled.current = true;
    }
  }, [loader]);

  const observedRef = useViewTracking<HTMLAnchorElement>({
    ref: useViewObserver<HTMLAnchorElement>({
      options: ViewObserverPreset['viewport-asap'],
      onView: () => {
        setDiscoverShownPostCursor(additionalParams.rank);
      },
    }),
    onView: useCallback(() => {
      window.Tea('wap_related_post_show', {
        current_page_module: ModuleType.related_article,
        current_position: ClickPosition.related_post,
        group_id: groupId,
        media_id: article.author.userId,
        impr_id: article.imprId,
        article_class: articleClass,
        ...additionalParams,
      });
    }, []),
  });

  return (
    <Link
      className={cls('discover-immersive-article', {
        largeMarginBottom: beforeHotKeywordsModule || beforeHashtagArticleCards,
      })}
      ref={observedRef}
      to={postHref}
      onClick={(e) => e.preventDefault()}
    >
      <AuthorBar />
      {articleClass === EArticleClass.GALLERY && (
        <ArticleGallery
          article={article}
          articleRelatedPosts={articleRelatedPost}
          loadArticleRelated={loadArticleRelatedPost}
          enableIndexForRecommend
          layoutStrategy={EArticleGalleryLayoutStrategy.RECTANGLE}
        />
      )}
      {articleClass === EArticleClass.ARTICLE && <ArticleLargeImage />}
      {articleClass === EArticleClass.VIDEO && (
        <ArticleVideo articleRelatedPosts={articleRelatedPost} loadArticleRelated={loadArticleRelatedPost} />
      )}
      <ArticleInfoBar />
      <ArticleBody />
      {!beforeGuessWordsCards && <SAR />}
      <ArticleComments />
    </Link>
  );
};

export const ImmersivePostItem: FC<IImmersivePostItemProps> = (props) => {
  const {
    article,
    author,
    comments,
    rank,
    beforeHotKeywordsModule = false,
    beforeGuessWordsCards = false,
    beforeHashtagArticleCards = false,
  } = props;

  return (
    <ScopedProvider
      atomsWithHydration={[
        [ImmersivePostItemAtoms.$CurrentArticle, article],
        [ImmersivePostItemAtoms.$CurrentAuthor, author],
        [ImmersivePostItemAtoms.$CurrentComments, comments],
        [ImmersivePostItemAtoms.$RelatedShowAdditionalParams, { rank }],
        [ImmersivePostItemAtoms.$BeforeHotKeywordsModule, beforeHotKeywordsModule],
        [ImmersivePostItemAtoms.$BeforeGuessWordsCards, beforeGuessWordsCards],
        [ImmersivePostItemAtoms.$BeforeHashtagArticleCards, beforeHashtagArticleCards],
      ]}
    >
      <ImmersivePostItemImpl />
    </ScopedProvider>
  );
};
